import { createContext, useContext, useEffect, useState } from "react";
import { api, LEGACY_TOKEN_KEY, SESSION_HINT_KEY } from "./api";

const AuthContext = createContext(null);

const storageGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const storageSet = (key, value) => { try { localStorage.setItem(key, value); } catch { /* storage unavailable */ } };
const storageRemove = (key) => { try { localStorage.removeItem(key); } catch { /* storage unavailable */ } };
// Só um indicador de que há sessão (o token em si fica no cookie HttpOnly).
const hasSessionHint = () => Boolean(storageGet(SESSION_HINT_KEY) || storageGet(LEGACY_TOKEN_KEY));

// Contas que entraram antes da sessão em cookie: troca o token antigo pelo cookie
// e apaga o token do navegador.
async function migrateLegacyToken() {
  const legacy = storageGet(LEGACY_TOKEN_KEY);
  if (!legacy) return;
  try {
    await api.post("/auth/session", null, { headers: { Authorization: `Bearer ${legacy}` } });
    storageSet(SESSION_HINT_KEY, "1");
  } finally {
    storageRemove(LEGACY_TOKEN_KEY);
  }
}
// Last profile the server confirmed. Lets a returning person see the app
// right away while the session is re-checked in the background (the server
// may take a while to wake up).
const CACHE_KEY = "lumine_session_cache";

const readCache = () => {
  try {
    if (!hasSessionHint()) return null;
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (user, workspace) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ user, workspace })); } catch { /* storage unavailable */ }
};

export const clearSessionCache = () => {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* storage unavailable */ }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A sleeping server answers slowly or with 502/503 while it starts.
// Those are retried; a 401 (session rejected) is not.
const isRetryable = (err) => {
  const status = err?.response?.status;
  return !status || status >= 500 || err?.code === "ECONNABORTED";
};

async function fetchProfile() {
  const delays = [0, 2000, 5000, 10000];
  let lastError;
  for (const delay of delays) {
    if (delay) await wait(delay);
    try {
      const { data } = await api.get("/auth/me", { timeout: 25000 });
      return data;
    } catch (err) {
      lastError = err;
      if (!isRetryable(err)) break;
    }
  }
  throw lastError;
}

export function AuthProvider({ children }) {
  const cached = readCache();
  const [user, setUser] = useState(cached?.user ?? null);
  const [workspace, setWorkspace] = useState(cached?.workspace ?? null);
  // With a cached profile the app renders immediately.
  const [loading, setLoading] = useState(() => !cached && hasSessionHint());

  const refresh = async () => {
    if (!hasSessionHint()) { clearSessionCache(); setLoading(false); return; }
    try {
      await migrateLegacyToken();
      const data = await fetchProfile();
      setUser(data.user);
      setWorkspace(data.workspace);
      writeCache(data.user, data.workspace);
    } catch (err) {
      // Only a rejected session logs the person out. A temporary server error
      // keeps the session so the next screen can try again.
      if (err?.response?.status === 401) {
        storageRemove(SESSION_HINT_KEY);
        storageRemove(LEGACY_TOKEN_KEY);
        clearSessionCache();
        setUser(null);
        setWorkspace(null);
      } else console.error("Falha ao carregar o perfil", err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storageSet(SESSION_HINT_KEY, "1");
    setUser(data.user);
    await refresh();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    storageSet(SESSION_HINT_KEY, "1");
    setUser({ ...data.user, onboarding_status: "PENDING", subscription_required: true, subscription_access: false });
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* o cookie expira sozinho */ }
    storageRemove(SESSION_HINT_KEY);
    storageRemove(LEGACY_TOKEN_KEY);
    clearSessionCache();
    setUser(null); setWorkspace(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, workspace, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
